import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

type StagedSlide = {
  slide_id: string
  slide_number: number
  title: string
  bullet_content: string[]
  image_src: string
  audio_src: string
}

type StagedQuestion = {
  id: string
  type: string
  question: string
  options: Array<{ id: string; text: string }>
  correct_answers: string[]
  rationale: string
  feedback?: {
    correct?: string
    incorrect?: string
  }
}

type StagedManifest = {
  title: string
  slides: StagedSlide[]
  quiz: {
    passing_score_percent: number
    questions: StagedQuestion[]
  }
}

const MANIFEST_URL = '/training-media/med-005/manifest.json'
const AUTO_ADVANCE_NARRATION_DELAY_MS = 1200

function normalizeOptionLabel(value: string): string {
  if (/^true\.?\s+true$/i.test(value) || /^true$/i.test(value)) return 'True'
  if (/^false\.?\s+false$/i.test(value) || /^false$/i.test(value)) return 'False'
  return value
}

function isQuestionCorrect(question: StagedQuestion, selected: Set<string>): boolean {
  const expected = new Set(question.correct_answers)
  if (expected.size !== selected.size) return false
  return Array.from(expected).every((answer) => selected.has(answer))
}

function formatNarrationTime(value: number): string {
  if (!Number.isFinite(value) || value < 0) return '0:00'
  const minutes = Math.floor(value / 60)
  const seconds = Math.floor(value % 60).toString().padStart(2, '0')
  return `${minutes}:${seconds}`
}

function renderInlineMarkup(value: string) {
  return value.split(/(\*\*[^*]+\*\*)/g).map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={`${part}-${index}`}>{part.slice(2, -2)}</strong>
    }
    return part
  })
}

export function Med005StagedLearnerPage() {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const autoPlayTimerRef = useRef<number | null>(null)
  const [manifest, setManifest] = useState<StagedManifest | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [slideIndex, setSlideIndex] = useState(0)
  const [completedSlides, setCompletedSlides] = useState<Set<number>>(() => new Set())
  const [hasStartedPlayback, setHasStartedPlayback] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [volume, setVolume] = useState(0.85)
  const [audioProgress, setAudioProgress] = useState({ currentTime: 0, duration: 0 })
  const [showQuiz, setShowQuiz] = useState(false)
  const [answers, setAnswers] = useState<Record<string, Set<string>>>({})
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetch(MANIFEST_URL)
      .then((response) => {
        if (!response.ok) throw new Error('Training media is not available.')
        return response.json() as Promise<StagedManifest>
      })
      .then((payload) => {
        if (!cancelled) setManifest(payload)
      })
      .catch((error) => {
        if (!cancelled) setLoadError(error instanceof Error ? error.message : 'Training media could not be loaded.')
      })
    return () => {
      cancelled = true
    }
  }, [])

  const activeSlide = manifest?.slides[slideIndex] || null
  const isFirstSlide = slideIndex === 0
  const isLastSlide = manifest ? slideIndex === manifest.slides.length - 1 : false
  const canAdvance = completedSlides.has(slideIndex)
  const narrationProgressPercent = audioProgress.duration > 0
    ? Math.min(100, Math.max(0, (audioProgress.currentTime / audioProgress.duration) * 100))
    : 0

  function syncAudioProgress() {
    const audio = audioRef.current
    if (!audio) return
    setAudioProgress({
      currentTime: Number.isFinite(audio.currentTime) ? audio.currentTime : 0,
      duration: Number.isFinite(audio.duration) ? audio.duration : 0,
    })
  }

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    audio.volume = volume
  }, [volume, activeSlide?.audio_src])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !activeSlide || showQuiz) return
    if (autoPlayTimerRef.current) {
      window.clearTimeout(autoPlayTimerRef.current)
      autoPlayTimerRef.current = null
    }
    audio.pause()
    audio.currentTime = 0
    if (!hasStartedPlayback) return

    autoPlayTimerRef.current = window.setTimeout(() => {
      audio.play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false))
    }, AUTO_ADVANCE_NARRATION_DELAY_MS)

    return () => {
      if (autoPlayTimerRef.current) {
        window.clearTimeout(autoPlayTimerRef.current)
        autoPlayTimerRef.current = null
      }
    }
  }, [activeSlide, hasStartedPlayback, showQuiz])

  const score = useMemo(() => {
    if (!manifest) return null
    const correctCount = manifest.quiz.questions.filter((question) => isQuestionCorrect(question, answers[question.id] || new Set())).length
    const percent = manifest.quiz.questions.length > 0 ? Math.round((correctCount / manifest.quiz.questions.length) * 100) : 0
    return {
      correctCount,
      percent,
      passed: percent >= manifest.quiz.passing_score_percent,
    }
  }, [answers, manifest])

  function playOrPause() {
    const audio = audioRef.current
    if (!audio) return
    setHasStartedPlayback(true)
    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
      return
    }
    audio.play()
      .then(() => setIsPlaying(true))
      .catch(() => setIsPlaying(false))
  }

  function handleAudioEnded() {
    setIsPlaying(false)
    syncAudioProgress()
    setCompletedSlides((current) => new Set(current).add(slideIndex))
    if (!manifest) return
    if (slideIndex >= manifest.slides.length - 1) {
      setShowQuiz(true)
      return
    }
    setAudioProgress({ currentTime: 0, duration: 0 })
    setSlideIndex((index) => Math.min(manifest.slides.length - 1, index + 1))
  }

  function goToPreviousSlide() {
    setShowQuiz(false)
    setAudioProgress({ currentTime: 0, duration: 0 })
    setSlideIndex((index) => Math.max(0, index - 1))
  }

  function goToNextSlide() {
    if (!manifest || !canAdvance) return
    setAudioProgress({ currentTime: 0, duration: 0 })
    setSlideIndex((index) => Math.min(manifest.slides.length - 1, index + 1))
  }

  function selectAnswer(question: StagedQuestion, optionId: string, checked: boolean) {
    setSubmitted(false)
    setAnswers((current) => {
      const next = { ...current }
      const optionSet = new Set(next[question.id] || [])
      if (question.type === 'SATA') {
        if (checked) optionSet.add(optionId)
        else optionSet.delete(optionId)
      } else {
        optionSet.clear()
        optionSet.add(optionId)
      }
      next[question.id] = optionSet
      return next
    })
  }

  if (loadError) {
    return (
      <div className="training-workspace min-h-screen p-4 md:p-8">
        <section className="training-panel mx-auto max-w-3xl p-5 text-sm text-rose-700">{loadError}</section>
      </div>
    )
  }

  if (!manifest || !activeSlide) {
    return (
      <div className="training-workspace min-h-screen p-4 md:p-8">
        <section className="training-panel mx-auto max-w-3xl p-5 text-sm text-slate-600">Loading training media...</section>
      </div>
    )
  }

  return (
    <div className="training-workspace min-h-screen p-3 md:p-6">
      <main className="mx-auto max-w-6xl space-y-4">
        <header className="training-panel flex flex-wrap items-center justify-between gap-3 p-4">
          <div>
            <p className="training-eyebrow">MED-005</p>
            <h1 className="section-title mt-1">{manifest.title}</h1>
          </div>
          <Link to="/training/learner" className="btn-ghost px-3 py-2 text-xs">
            Training
          </Link>
        </header>

        {!showQuiz ? (
          <section className="training-panel overflow-hidden p-0">
            <div className="grid min-w-0 min-h-[min(720px,calc(100vh-180px))] w-full max-w-full grid-rows-[1fr_auto]">
              <article className="grid min-w-0 w-[calc(100vw-3.625rem)] max-w-full gap-0 md:w-full lg:grid-cols-[minmax(0,1.12fr)_minmax(320px,0.88fr)]">
                <div className="min-w-0 min-h-[280px] w-full max-w-full bg-slate-950">
                  <img src={activeSlide.image_src} alt="" className="h-full min-h-[280px] w-full object-cover" />
                </div>
                <div className="flex min-w-0 min-h-[280px] w-full max-w-full flex-col justify-center p-5 md:p-8">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">
                    Slide {activeSlide.slide_number} of {manifest.slides.length}
                  </p>
                  <h2 className="mt-3 font-brand text-2xl font-bold leading-tight text-[var(--brand-deep)] md:text-3xl">{activeSlide.title}</h2>
                  <ul className="mt-6 min-w-0 space-y-4 text-base font-semibold leading-7 text-slate-800 md:text-lg">
                    {activeSlide.bullet_content.map((bullet) => (
                      <li key={bullet} className="min-w-0 max-w-full break-words rounded-lg border border-slate-200 bg-white px-4 py-3">{renderInlineMarkup(bullet)}</li>
                    ))}
                  </ul>
                </div>
              </article>

              <div className="border-t border-[color:var(--training-border)] bg-white px-3 py-2">
                <audio
                  ref={audioRef}
                  src={activeSlide.audio_src}
                  preload="auto"
                  onDurationChange={syncAudioProgress}
                  onEnded={handleAudioEnded}
                  onLoadedMetadata={syncAudioProgress}
                  onPause={() => setIsPlaying(false)}
                  onPlay={() => setIsPlaying(true)}
                  onTimeUpdate={syncAudioProgress}
                />
                <div className="flex min-w-0 flex-nowrap items-center gap-2">
                  {!isFirstSlide ? (
                    <button type="button" className="btn-ghost shrink-0 px-2.5 py-1.5 text-xs" onClick={goToPreviousSlide}>
                      Previous
                    </button>
                  ) : null}
                  <button type="button" className="btn-primary shrink-0 px-3 py-1.5 text-xs" onClick={playOrPause}>
                    {isPlaying ? 'Pause' : 'Play'}
                  </button>
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <label className="flex min-w-0 items-center gap-2 text-xs font-bold text-slate-600">
                      <span className="shrink-0">Volume</span>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={volume}
                        onChange={(event) => setVolume(Number(event.target.value))}
                        className="min-w-0 flex-1 accent-[var(--brand-primary)]"
                      />
                    </label>
                    <div className="flex min-w-0 items-center gap-2 text-[11px] font-bold text-slate-500" aria-label={`Narration progress ${formatNarrationTime(audioProgress.currentTime)} of ${formatNarrationTime(audioProgress.duration)}`}>
                      <div
                        className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-200"
                        role="progressbar"
                        aria-label="Narration progress"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={Math.round(narrationProgressPercent)}
                      >
                        <div className="h-full rounded-full bg-[var(--brand-primary)] transition-[width] duration-150" style={{ width: `${narrationProgressPercent}%` }} />
                      </div>
                      <span className="shrink-0 tabular-nums">
                        {formatNarrationTime(audioProgress.currentTime)} / {formatNarrationTime(audioProgress.duration)}
                      </span>
                    </div>
                  </div>
                  {!isLastSlide ? (
                    <button type="button" className="btn-ghost shrink-0 px-2.5 py-1.5 text-xs disabled:cursor-not-allowed disabled:opacity-45" disabled={!canAdvance} onClick={goToNextSlide}>
                      Next
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </section>
        ) : (
          <section className="training-panel p-4 md:p-6" aria-label="In-module quiz">
            <div>
              <p className="training-eyebrow">MED-005</p>
              <h2 className="section-title mt-1">Quiz</h2>
              <p className="mt-2 text-sm font-semibold text-slate-600">Complete the in-module quiz to finish this training item.</p>
            </div>
            <div className="mt-5 space-y-4">
              {manifest.quiz.questions.map((question, questionIndex) => {
                const selected = answers[question.id] || new Set<string>()
                const correct = isQuestionCorrect(question, selected)
                return (
                  <article key={question.id} className="rounded-lg border border-[color:var(--training-border)] bg-white p-4">
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">Question {questionIndex + 1}</p>
                    <p className="mt-2 text-base font-bold leading-6 text-slate-900">{question.question}</p>
                    <div className="mt-4 space-y-2">
                      {question.options.map((option) => {
                        const inputType = question.type === 'SATA' ? 'checkbox' : 'radio'
                        const checked = selected.has(option.id)
                        return (
                          <label key={option.id} className="flex items-start gap-3 rounded-lg border border-slate-200 px-3 py-3 text-sm font-semibold text-slate-700">
                            <input
                              type={inputType}
                              name={`med-005-${question.id}`}
                              checked={checked}
                              onChange={(event) => selectAnswer(question, option.id, event.target.checked)}
                            />
                            <span>{normalizeOptionLabel(option.text)}</span>
                          </label>
                        )
                      })}
                    </div>
                    {submitted ? (
                      <div className={correct ? 'mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800' : 'mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900'}>
                        {correct ? question.feedback?.correct || 'Correct.' : question.feedback?.incorrect || question.rationale}
                      </div>
                    ) : null}
                  </article>
                )
              })}
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button type="button" className="btn-primary px-4 py-2 text-sm" onClick={() => setSubmitted(true)}>
                Submit
              </button>
              {submitted && score ? (
                <p className={score.passed ? 'text-sm font-bold text-emerald-700' : 'text-sm font-bold text-amber-800'}>
                  {score.percent}% ({score.correctCount}/{manifest.quiz.questions.length})
                </p>
              ) : null}
            </div>
          </section>
        )}
      </main>
    </div>
  )
}
